import type { Identifier } from "ra-core";

import type {
  WhatsAppAssignMode,
  WhatsAppBusinessUnit,
  WhatsAppConversationFlagsRow,
  WhatsAppUnclassifiedMessageRow,
  WhatsAppUnclassifiedRow,
} from "../whatsappTypes";
import { buildInboxRows } from "./whatsappInboxRules";

/**
 * Almikia Epic EW3 (TASK-095): pure rules of the 'Sin clasificar' tray and the personal-numbers
 * list, shared by the Supabase provider, the FakeRest demo and the screens. The canonical-phone
 * rule mirrors `private.whatsapp_canonical_phone` / `private.whatsapp_personal_input_phone`.
 */

/** The carousel only takes BPO / Almikia leads (mirrors `private.deal_enters_carousel`). */
export const CAROUSEL_LEAD_TYPES: readonly string[] = ["bpo", "almikia"];

/** The bulk-load RPC refuses more entries than this per call. */
export const MAX_PERSONAL_NUMBERS_PER_LOAD = 5000;

/** The tray's extra conversation columns (holder-only data): number, profile name, known contact. */
export type UnclassifiedConversation = WhatsAppConversationFlagsRow & {
  phone?: string | null;
  profile_name?: string | null;
  contact_id?: Identifier | null;
};

/**
 * Formats a Mexican canonical number (+52 and ten digits) as '+52 xx xxxx xxxx'; any other shape
 * is returned unchanged. Only the tray calls this: no other screen shows a number.
 */
export const formatSenderPhone = (phone: string): string => {
  const match = /^\+52(\d{2})(\d{4})(\d{4})$/.exec(phone);
  return match ? `+52 ${match[1]} ${match[2]} ${match[3]}` : phone;
};

/**
 * Builds the tray rows: newest message as preview, unread count, the echo origin, the sender
 * number and profile name, and the known contact (`contactNames` maps contact id to its name).
 */
export const buildUnclassifiedRows = (
  conversations: readonly UnclassifiedConversation[],
  messages: readonly WhatsAppUnclassifiedMessageRow[],
  contactNames: ReadonlyMap<string, string> = new Map(),
): WhatsAppUnclassifiedRow[] =>
  buildInboxRows(conversations, new Map(), messages, new Map()).map((row) => {
    const newest = messages
      .filter((m) => String(m.conversation_id) === String(row.conversationId))
      .sort((a, b) => b.message_at.localeCompare(a.message_at))[0];
    const conversation = conversations.find((c) =>
      sameId(row.conversationId, c.id),
    );
    const contactId = conversation?.contact_id ?? null;
    return {
      conversationId: row.conversationId,
      unreadCount: row.unreadCount,
      lastMessage: row.lastMessage,
      lastOrigin: newest?.origin ?? null,
      lastMessageAt: row.lastMessageAt,
      senderPhone: conversation?.phone ?? null,
      profileName: conversation?.profile_name?.trim() || null,
      knownContact:
        contactId == null
          ? null
          : {
              id: contactId,
              name: contactNames.get(String(contactId)) ?? null,
            },
    };
  });

export type ClassifyDraft = {
  /** Defaults to 'bpo'. Corretaje has no project and no carousel. */
  businessUnit: WhatsAppBusinessUnit;
  projectId: string;
  leadType: string;
  leadSourceId: string;
  assignMode: WhatsAppAssignMode;
  salesId: string;
};

export type ClassifyErrorKey =
  | "project_required"
  | "type_required"
  | "source_required"
  | "advisor_required"
  | "carousel_not_applicable";

/** The validation the dialog runs before submit; an empty result means the draft is valid. */
export const validateClassifyDraft = (
  draft: ClassifyDraft,
): Partial<
  Record<"project" | "type" | "source" | "advisor", ClassifyErrorKey>
> => {
  const errors: Partial<
    Record<"project" | "type" | "source" | "advisor", ClassifyErrorKey>
  > = {};
  if (draft.businessUnit === "bpo" && !draft.projectId) {
    errors.project = "project_required";
  }
  if (!draft.leadType) errors.type = "type_required";
  if (!draft.leadSourceId) errors.source = "source_required";
  if (draft.assignMode === "advisor" && !draft.salesId) {
    errors.advisor = "advisor_required";
  }
  if (
    draft.businessUnit === "bpo" &&
    draft.assignMode === "carousel" &&
    draft.leadType &&
    !CAROUSEL_LEAD_TYPES.includes(draft.leadType)
  ) {
    errors.advisor = "carousel_not_applicable";
  }
  return errors;
};

/**
 * Splits pasted / uploaded text into raw entries: one per line, comma or semicolon. A first entry
 * with no digit at all is a header and is dropped; blank entries are ignored.
 */
export const parsePersonalNumbers = (text: string): string[] => {
  const entries = text
    .split(/[\r\n,;]+/)
    .map((entry) => entry.trim())
    .filter((entry) => entry !== "");
  return entries.length > 0 && !/\d/.test(entries[0])
    ? entries.slice(1)
    : entries;
};

/**
 * Mirrors the server normalizer: a leading '+' is kept, a bare 10-digit number is Mexican, then
 * a Mexican 52 / 521 + 10 digits becomes '+52' + 10 digits. Returns null when it is not a valid
 * E.164-shaped number (8 to 15 digits).
 */
export const canonicalPersonalPhone = (raw: string): string | null => {
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, "");
  let candidate: string;
  if (trimmed.startsWith("+")) candidate = `+${digits}`;
  else if (/^\d{10}$/.test(digits)) candidate = `52${digits}`;
  else candidate = digits;
  const candidateDigits = candidate.replace(/\D/g, "");
  const mx = /^521?(\d{10})$/.exec(candidateDigits);
  const canonical = mx ? `+52${mx[1]}` : candidate;
  return /^\+\d{8,15}$/.test(canonical) ? canonical : null;
};

/** The last 10 digits, the shape the lead matcher compares (Mexican numbers). */
export const lastTenDigits = (phone: string): string =>
  phone.replace(/\D/g, "").slice(-10);

export const sameId = (a: Identifier | null | undefined, b: Identifier) =>
  a != null && String(a) === String(b);
